import React from 'react';

import ContactForm from '$partials/forms/contact-form';
import { Footer, Meta } from '$partials/global';
import ModuleDispatcher from '$partials/modules/dispatcher.jsx';
import CodeSnippet from '$partials/global/code-snippet';
import { IconChevronSprite } from '$partials/icons/index.js';
import Button from '$partials/ui/button.jsx';
import { useLocale } from '$contexts/locale.jsx';
import Image from '$partials/media/image';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').ContactDetailPage}
 */

const ContactDetailPage = ({ data, domain }) => {
    const page = data?.page;
    const { formatURI, translate: t } = useLocale();

    return (
        <>
            <Meta page={page} />

            <main
                id='main-content'>
                <div className='min-h-screen'>
                    <div className='layout-container layout-site-outer-padding pb-24 pt-80 md:pb-48 md:pt-[152px]'>
                        <div className='col-wrapper flex flex-col gap-y-70 md:flex-row'>
                            <div className='col-padding md:w-6/12 flex flex-col gap-y-20'>
                                {page?.listingEyebrowLogo && (
                                    <div className='w-48'>
                                        <Image
                                            containerClassName='w-48 overflow-hidden'
                                            height={page.listingEyebrowLogo.height}
                                            loadWidth={48}
                                            src={page.listingEyebrowLogo.src}
                                            width={page.listingEyebrowLogo.width}/>
                                    </div>
                                )}

                                <h1 className='type-style-3-extended text-text-primary'>
                                    {page?.pageTitle}
                                </h1>

                                {/* body copy */}
                                {page?.bodyCopy && (
                                    <p className='text-text-secondary type-style-body md:w-5/6'>
                                        {page.bodyCopy}
                                    </p>
                                )}

                                {/* cta buttons */}
                                {page?.ctaLink && (
                                    <Button
                                        as='a'
                                        className='button button--outline h-min w-min'
                                        href={formatURI(page.ctaLink)}>
                                        <span className='button__label--animate'>
                                            <span>{page.ctaLabel}</span>
                                        </span>
                                        <span className='button__icon button__icon--animate'>
                                            <IconChevronSprite />
                                        </span>
                                    </Button>
                                )}
                            </div>
                            {/* Form */}
                            <section className='w-full md:w-6/12'>
                                {page?.formEmbedCode && (
                                    <CodeSnippet html={page.formEmbedCode} />
                                )}
                                {data?.hubspotForm?.formId && (
                                    <ContactForm
                                        formConsentOptions={data.hubspotForm.formConsentOptions}
                                        formEmail={page?.formEmailAddress}
                                        formFields={data.hubspotForm.formFields}
                                        formId={data.hubspotForm.formId}
                                        slug={page.slug}
                                        title={page.pageTitle} />
                                )}

                                {(page?.formEmailAddress && !data?.hubspotForm?.formId) && (
                                    <ContactForm
                                        formEmail={page?.formEmailAddress}
                                        slug={page.slug}
                                        title={page.pageTitle} />
                                )}
                            </section>
                        </div>
                    </div>
                    {/* Content */}
                    {(page?.modules?.items && page?.modules?.items?.length > 0) && (
                        page.modules.items.map((itm, i) => {
                            if (!itm) { return null; }
                            return (
                                <ModuleDispatcher
                                    key={i}
                                    module={itm}
                                    previousTypename={page.modules.items[i - 1]?.__typename ?? null}
                                    nextTypename={page.modules.items[i + 1]?.__typename ?? null}/>
                            );
                        })
                    )}
                    <div className='pt-40 md:pt-60'>
                        <Footer
                            domain={domain}
                            menu={data?.footer} />
                    </div>
                </div>
            </main>
        </>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default ContactDetailPage;
